// Typed team-member endpoint functions. Backed by the canonical
// /users/* endpoints from specs/tier-04-interface/user-management.api.yaml.
//
// The T2 entity is `account` per CHG-0034 account-first identity model
// (specs/tier-02-domain/account.entity.yaml), but the API kept the v3
// /users path namespace; this client uses the API path verbatim and
// exposes the entity-hint canonical name (TeamMember) on the consumer
// surface. Per CHG-1235 + CHG-1249 plan.
//
// /users/{id}/activity, /users/{id}/permissions, /users/{id}/sessions are
// forward-promised sub-resources — the canonical T4 spec lists them as
// deferred. This client bridges them today by reading from the primary
// GET /users/{id} response (enriched fields) plus a tiny client-side
// projection until the canonical endpoints land. Forward-compatible: when
// each canonical endpoint ships, swap the implementation without touching
// page code or generator-slot configs.

import { apiClient } from "./client";
import type { PagedEnvelope } from "./types";

export type TeamMemberStatus = "active" | "pending" | "disabled" | "locked";

export interface TeamMember {
  id: string;
  organization_id: string;
  full_name: string;
  email: string;
  // Derived display label resolved from the user_role join by the API
  // (per account.entity-hint.yaml#column_overrides[role].field_source: derived).
  role: string | null;
  status: TeamMemberStatus;
  /**
   * @deprecated CHG-2955 (UM-R9) — compatibility alias per the authoritative
   * phone read-projection rule (tier-04 user-management.api.yaml AccountRead):
   * the backend serializes canonical `phone_e164` here when one exists, else
   * the legacy raw value. New code reads `phone_e164` + `phone_verified_at`.
   */
  phone_number?: string | null;
  /** Canonical E.164 phone (synced User.phone_e164; written only by the OTP confirm flows). */
  phone_e164?: string | null;
  phone_verified_at?: string | null;
  last_login_at: string | null;
  mfa_enabled: boolean;
  failed_login_count: number;
  locked_until: string | null;
  created_at: string;
  updated_at: string;
}

export type TeamMemberPagedEnvelope = PagedEnvelope<TeamMember>;

export interface TeamMemberActivityEntry {
  id: string;
  kind: string;
  occurred_at: string;
  description: string;
}

export interface TeamMemberPermissionEntry {
  id: string;
  code: string;
  source: "role" | "direct";
  label?: string;
}

// CHG-2958 (Slice J / UM-R12) — REAL shape from the live endpoint
// GET /platform/members/accounts/{id}/sessions (CHG-2957 / Slice H;
// routers/platform_members.py list_account_sessions). Replaces the
// forward-promised placeholder shape, which had no consumers.
export interface TeamMemberSessionEntry {
  id: string;
  channel: string;
  issued_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  ip_address: string | null;
  user_agent: string | null;
  is_active: boolean;
}

export interface ListTeamMembersParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: TeamMemberStatus;
  role?: string;
}

export async function listTeamMembers(
  params: ListTeamMembersParams = {},
): Promise<{ items: TeamMember[]; total: number }> {
  const r = await apiClient().get<TeamMemberPagedEnvelope>("/users", {
    params: {
      offset: (params.page ?? 0) * (params.pageSize ?? 50),
      limit: params.pageSize ?? 50,
      sort: params.sortField
        ? `${params.sortField}:${params.sortDirection ?? "asc"}`
        : undefined,
      q: params.search,
      status: params.status,
      role: params.role,
    },
  });
  return { items: r.data.items, total: r.data.total };
}

export async function getTeamMember(id: string): Promise<TeamMember> {
  const r = await apiClient().get<TeamMember>(`/users/${id}`);
  return r.data;
}

/**
 * Remove an organization membership while preserving the underlying account,
 * person, and donation history. The API soft-deletes the membership and
 * revokes its active sessions; it also rejects self-removal and loss of the
 * organization's final administrator.
 */
export async function removeTeamMember(id: string): Promise<TeamMember> {
  const r = await apiClient().delete<TeamMember>(`/users/${id}`);
  return r.data;
}

// Forward-promised: canonical /users/{id}/activity ships later. Until then
// the page renders zero rows + a "No activity yet" empty state served by
// the DetailPage tab renderer.
export async function getTeamMemberActivity(
  id: string,
): Promise<TeamMemberActivityEntry[]> {
  // Touch id so the function signature stays accurate; once the canonical
  // endpoint ships, replace with a real apiClient().get<...>(`/users/${id}/activity`).
  void id;
  return [];
}

// Forward-promised: same pattern as activity.
export async function getTeamMemberPermissions(
  id: string,
): Promise<TeamMemberPermissionEntry[]> {
  void id;
  return [];
}

// CHG-2958 (Slice J / UM-R12) — wired to the live endpoint shipped in
// CHG-2957 (Slice H). NOTE the platform-members path namespace: the
// canonical /users/{id}/sessions sub-resource never shipped; the entity
// hint's Sessions tab data_source points here deliberately
// (team-member.entity-hint.yaml). Callers must gate on
// alora-giving.platform.member.read (NOT user.read) — the BE enforces it
// on this route.
export async function getTeamMemberSessions(
  id: string,
): Promise<TeamMemberSessionEntry[]> {
  const r = await apiClient().get<TeamMemberSessionEntry[]>(
    `/platform/members/accounts/${id}/sessions`,
  );
  return r.data;
}

export const teamMembersApi = {
  listTeamMembers,
  getTeamMember,
  removeTeamMember,
  getTeamMemberActivity,
  getTeamMemberPermissions,
  getTeamMemberSessions,
};
